/**
 * TreeHealthModal — surfaces the deterministic consistency checks from
 * GET /trees/:id/health and lets the owner apply one-click fixes (flip a
 * reversed parent link, remove an impossible/duplicate link, merge duplicate
 * records). Re-checks after each fix and refreshes the tree view.
 */
import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../services/api.js'
import './TreeHealthModal.css'

const SEV_LABEL = { high: 'Problem', medium: 'Check', low: 'Note' }

function personLine(p) {
  return `${p.name}${p.year ? ` (b. ${p.year})` : ''}`
}

export default function TreeHealthModal({ treeId, onClose, onApplied }) {
  const [loading, setLoading] = useState(true)
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)        // issue index being fixed
  const [keepChoice, setKeepChoice] = useState({}) // issueIndex -> personId to keep
  const [ai, setAi] = useState({ ran: false, loading: false, candidates: [], error: null })

  const run = useCallback(() => {
    setLoading(true)
    setError(null)
    api.get(`/trees/${treeId}/health`)
      .then(setReport)
      .catch(e => setError(e.message || 'Check failed'))
      .finally(() => setLoading(false))
  }, [treeId])

  useEffect(() => { run() }, [run])

  async function applyFix(issue, idx) {
    setBusy(idx)
    setError(null)
    try {
      if (issue.fix === 'flip') {
        await api.post(`/trees/${treeId}/health/flip-edge`, { relationship_id: issue.relationship_id })
      } else if (issue.fix === 'remove') {
        if (!confirm('Remove this link? This deletes the relationship from your tree (you can re-add it).')) { setBusy(null); return }
        await api.post(`/trees/${treeId}/health/remove-edge`, { relationship_id: issue.relationship_id })
      } else if (issue.fix === 'merge') {
        const keepId = keepChoice[idx] || issue.suggested_keep_id || issue.people[0].id
        const others = issue.people.filter(p => p.id !== keepId)
        if (!confirm(`Merge ${others.length + 1} records into one? This combines them everywhere and can't be auto-undone.`)) { setBusy(null); return }
        // Merge each duplicate into the kept record.
        for (const o of others) {
          await api.post(`/trees/${treeId}/health/merge`, { keep_id: keepId, merge_id: o.id })
        }
      }
      onApplied?.()
      // Drop a just-merged AI candidate from its list; re-check the rules.
      if (typeof idx === 'string' && idx.startsWith('ai')) {
        setAi(s => ({ ...s, candidates: s.candidates.filter((_, k) => `ai${k}` !== idx) }))
      }
      run()
    } catch (e) {
      setError(e.message || 'Could not apply the fix')
    } finally {
      setBusy(null)
    }
  }

  async function runAI() {
    setAi(s => ({ ...s, loading: true, error: null }))
    try {
      const res = await api.get(`/trees/${treeId}/health/lookalikes`)
      setAi({ ran: true, loading: false, candidates: res.candidates || [], error: null })
    } catch (e) {
      setAi(s => ({ ...s, ran: true, loading: false, error: e.message || 'AI check failed' }))
    }
  }

  const issues = report?.issues || []

  return createPortal((
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal tree-health-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Tree health</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="tree-health-modal__body">
          {loading && <div className="tree-health-modal__muted">Checking your tree…</div>}
          {error && <div className="tree-health-modal__error">{error}</div>}

          {!loading && !error && issues.length === 0 && (
            <div className="tree-health-modal__ok">
              ✓ No problems found across {report?.person_count ?? 0} people. Your tree looks consistent.
            </div>
          )}

          {!loading && issues.length > 0 && (
            <>
              <p className="tree-health-modal__intro">
                Found <strong>{issues.length}</strong> {issues.length === 1 ? 'thing' : 'things'} to look at.
                Fixes apply to your tree and update the view.
              </p>

              <div className="tree-health-modal__list">
                {issues.map((issue, idx) => (
                  <div key={idx} className={`tree-health-modal__issue sev-${issue.severity}`}>
                    <div className="tree-health-modal__issue-head">
                      <span className={`tree-health-modal__sev sev-${issue.severity}`}>{SEV_LABEL[issue.severity]}</span>
                      <span className="tree-health-modal__msg">{issue.message}</span>
                    </div>

                    {/* Duplicate: choose which record to keep */}
                    {issue.fix === 'merge' && (
                      <div className="tree-health-modal__keep">
                        {issue.people.map(p => (
                          <label key={p.id} className="tree-health-modal__keep-row">
                            <input
                              type="radio"
                              name={`keep-${idx}`}
                              checked={(keepChoice[idx] || issue.suggested_keep_id || issue.people[0].id) === p.id}
                              onChange={() => setKeepChoice(s => ({ ...s, [idx]: p.id }))}
                            />
                            <span>Keep <strong>{personLine(p)}</strong></span>
                          </label>
                        ))}
                      </div>
                    )}

                    <div className="tree-health-modal__issue-foot">
                      {issue.fix ? (
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={busy === idx}
                          onClick={() => applyFix(issue, idx)}
                        >
                          {busy === idx ? 'Applying…'
                            : issue.fix === 'flip' ? 'Flip link'
                            : issue.fix === 'remove' ? 'Remove link'
                            : 'Merge records'}
                        </button>
                      ) : (
                        <span className="tree-health-modal__review">Review manually — no safe auto-fix</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!loading && !error && (
            <div className="tree-health-modal__ai">
              <div className="tree-health-modal__ai-head">
                <span>Possible duplicates (AI)</span>
                <button className="btn btn-sm btn-ghost" onClick={runAI} disabled={ai.loading}>
                  {ai.loading ? 'Looking…' : ai.ran ? 'Run again' : '🔍 Find look-alikes'}
                </button>
              </div>
              <p className="tree-health-modal__ai-note">
                Finds same-person records the rules can't — spelling variants, or the same
                name across generations (a grandfather and grandson, which it leaves alone).
              </p>
              {ai.error && <div className="tree-health-modal__error">{ai.error}</div>}
              {ai.ran && !ai.loading && ai.candidates.length === 0 && (
                <div className="tree-health-modal__muted">No look-alikes found.</div>
              )}
              {ai.candidates.map((c, i) => {
                const idx = `ai${i}`
                const keepId = keepChoice[idx] || c.suggested_keep_id || c.people[0].id
                return (
                  <div key={idx} className="tree-health-modal__issue sev-medium">
                    <div className="tree-health-modal__issue-head">
                      <span className="tree-health-modal__sev sev-medium">{c.confidence}</span>
                      <span className="tree-health-modal__msg">{c.reason}</span>
                    </div>
                    <div className="tree-health-modal__keep">
                      {c.people.map(p => (
                        <label key={p.id} className="tree-health-modal__keep-row">
                          <input
                            type="radio"
                            name={`keep-${idx}`}
                            checked={keepId === p.id}
                            onChange={() => setKeepChoice(s => ({ ...s, [idx]: p.id }))}
                          />
                          <span>Keep <strong>{personLine(p)}</strong></span>
                        </label>
                      ))}
                    </div>
                    <div className="tree-health-modal__issue-foot">
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={busy === idx}
                        onClick={() => applyFix(
                          { fix: 'merge', people: c.people, suggested_keep_id: c.suggested_keep_id }, idx)}
                      >
                        {busy === idx ? 'Applying…' : 'Merge records'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body)
}
